import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@apps/web/lib/utils';

type SettingsActionBarContextValue = {
    readonly host: HTMLElement | null;
    readonly portalClassName: string;
};

const SettingsActionBarContext = createContext<SettingsActionBarContextValue>({
    host: null,
    portalClassName: 'w-full',
});

type SettingsActionBarProviderProps = {
    readonly children: ReactNode;
    readonly host?: HTMLElement | null;
    readonly portalClassName?: string;
};

export function SettingsActionBarProvider({
    children,
    host,
    portalClassName,
}: SettingsActionBarProviderProps) {
    const parent = useContext(SettingsActionBarContext);
    const value = useMemo(
        () => ({
            host: host ?? parent.host,
            portalClassName: portalClassName ?? parent.portalClassName,
        }),
        [host, parent.host, parent.portalClassName, portalClassName],
    );

    return (
        <SettingsActionBarContext.Provider value={value}>
            {children}
        </SettingsActionBarContext.Provider>
    );
}

type SettingsActionBarProps = {
    readonly children: ReactNode;
    readonly message?: ReactNode;
    readonly className?: string;
    readonly portal?: boolean;
};

export function SettingsActionBar({
    children,
    message,
    className,
    portal = false,
}: SettingsActionBarProps) {
    const { host, portalClassName } = useContext(SettingsActionBarContext);

    const actionBar = (
        <div
            className={cn(
                'bg-background border-border border-t py-3 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]',
                !portal && 'sticky bottom-0 z-10',
                portal && portalClassName,
                className,
            )}
        >
            <div
                className={cn(
                    'flex min-w-0 flex-wrap items-center gap-3',
                    message === undefined ? 'justify-end' : 'justify-between',
                )}
            >
                {message}
                <div className="flex min-w-0 flex-wrap justify-end gap-2">
                    {children}
                </div>
            </div>
        </div>
    );

    if (portal) {
        return host === null ? actionBar : createPortal(actionBar, host);
    }

    return actionBar;
}
